// ============================================================================
// AFB-0075 / PR 2 — template authoring, inside "Manage template documents" on
// /money/envelopes.
//
// A template that carries a document (PR 1 / W-09) is not yet a packet member.
// Three more things have to be true, and none of them can be inferred from the
// PDF:
//
//   1. WHAT it is (document_type) and which onboarding checklist item it
//      satisfies (checklist_key), so completion can flip the right item.
//   2. WHO signs it, by ROLE. Emails are never stored on a template — that is
//      the whole reason a template is reusable — so this edits placeholders
//      that are resolved to real people at send time.
//   3. Which placed fields the SYSTEM fills and from where. A merge binding is
//      chosen from esign_field_bindings, never typed: that registry is the
//      single chokepoint enforcing the no-SSN / no-bank-details rule, and free
//      text would route straight around it.
//
// page_count is conspicuously absent from this editor. It is parsed from the
// PDF at upload and shown read-only — a hand-typed page count would silently
// offset every later document in a packet.
//
// AFB-0075 / PR 4 (2026-08-20) — "Place fields from this PDF's form". Antonela's
// onboarding PDFs already declare their own AcroForm widgets (107 on the base
// packet, 154 on the Colorado one), so the third thing above no longer starts
// from an empty page: the document's own /Rect for each widget becomes a
// proposed layout she REVIEWS. Nothing is written until she says so, the
// summary names everything that was refused and why, and an existing layout is
// never replaced without saying how many fields go with it. The rules and the
// arithmetic are pure and tested in ./acroform-placement; this file only asks
// for them and renders the answer.
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, Plus, Save, ScanLine, Trash2 } from 'lucide-react';
import {
  AcroFormPlacementResult, CHECKLIST_KEY_LABELS, DOCUMENT_TYPE_LABELS,
  EsignDocumentType, EsignTemplate, FieldBindingOption, FieldType, RecipientRole,
  TemplateField, TemplateFieldClass, TemplateRecipientRole,
  markFieldRestricted, normaliseFieldTagging, placeAcroFormFields,
  readTemplateAcroForm, selectableBindings, templateHasDocument,
  templatePacketBlockers,
} from './envelope-lib';
import { FIELD_LABEL } from './signer-field';
import { placementBlockers } from './field-canvas';
import { TemplateFieldCanvas } from './template-field-canvas';
import { errorMessage } from '../../shared/errors';

const FIELD_CLASSES: Array<{ value: TemplateFieldClass; label: string }> = [
  { value: 'signer', label: 'Signer fills it' },
  { value: 'merge', label: 'We prefill it' },
];

const ROLES: RecipientRole[] = ['SIGNER', 'APPROVER', 'CC'];

const AUTH_MODES: Array<{ value: TemplateRecipientRole['auth_mode']; label: string }> = [
  { value: 'emailed_link', label: 'Emailed link' },
  { value: 'email_otp', label: 'Emailed link + code' },
];

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text';
const LABEL = 'block text-[11px] font-medium text-tcs-text-secondary';
const PRIMARY =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-md bg-tcs-accent-ink px-3 text-xs font-semibold text-tcs-accent-on disabled:opacity-50';
const SECONDARY =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-tcs-border px-2 text-[11px] text-tcs-text-secondary disabled:opacity-50';

export interface TemplateAuthoringProps {
  template: EsignTemplate;
  /** Active rows of esign_field_bindings. Empty until they load. */
  bindings: FieldBindingOption[];
  /** Why the binding list is unavailable, if it is. */
  bindingsError: string | null;
  busy: boolean;
  onSaveMeta: (meta: {
    document_type: EsignDocumentType;
    checklist_key: string | null;
  }) => Promise<void>;
  onSaveRoles: (roles: TemplateRecipientRole[]) => Promise<void>;
  onSaveLayout: (layout: TemplateField[]) => Promise<void>;
}

/** recipient_roles is jsonb; anything that is not an array reads as none set. */
function rolesOf(template: EsignTemplate): TemplateRecipientRole[] {
  return Array.isArray(template.recipient_roles) ? template.recipient_roles : [];
}

function layoutOf(template: EsignTemplate): TemplateField[] {
  return Array.isArray(template.field_layout) ? template.field_layout : [];
}

/** "12 Sign here · 40 Text · 3 Date" — what a proposed layout is made of. */
function countByType(fields: TemplateField[]): Array<{ type: FieldType; count: number }> {
  const out: Array<{ type: FieldType; count: number }> = [];
  for (const f of fields) {
    const hit = out.find((o) => o.type === f.field_type);
    if (hit) hit.count += 1;
    else out.push({ type: f.field_type, count: 1 });
  }
  return out;
}

/** "page 2 · 32% across, 26% down" — enough to find the box on the document. */
function describePlacement(field: TemplateField): string {
  return (
    `page ${field.page} · ${Math.round(field.position_x * 100)}% across, ` +
    `${Math.round(field.position_y * 100)}% down`
  );
}

export function TemplateAuthoring({
  template, bindings, bindingsError, busy, onSaveMeta, onSaveRoles, onSaveLayout,
}: TemplateAuthoringProps) {
  const [docType, setDocType] = useState<EsignDocumentType>(template.document_type);
  const [checklistKey, setChecklistKey] = useState(template.checklist_key ?? '');
  const [roles, setRoles] = useState<TemplateRecipientRole[]>(rolesOf(template));
  const [layout, setLayout] = useState<TemplateField[]>(layoutOf(template));
  // AFB-0075 / PR 4 — the layout read off the PDF's own AcroForm. Held in state
  // and NOT saved: the entire point is that Antonela sees what it proposes, and
  // what it refused to propose, BEFORE anything replaces what is on the row.
  const [proposal, setProposal] = useState<AcroFormPlacementResult | null>(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);

  // The parent replaces the template object with the re-read row after every
  // save, so the drafts follow what is actually stored rather than drifting.
  useEffect(() => {
    setDocType(template.document_type);
    setChecklistKey(template.checklist_key ?? '');
    setRoles(rolesOf(template));
    setLayout(layoutOf(template));
    // A proposal describes the row as it was when it was generated — how many
    // fields it would replace, which roles it points at. After a save it is
    // stale, so it goes rather than sitting there looking current.
    setProposal(null);
    setReadError(null);
  }, [template]);

  const offered = selectableBindings(bindings);
  // Read off the SAVED row, never the drafts — "packet-ready" has to mean what
  // a packet built right now would find, not what is on screen unsaved.
  // Placement blockers sit alongside the tagging ones because they are the same
  // question from the author's side: is there anything on this template that a
  // packet could not resolve? A field on a page the document does not have, a
  // box off the paper, and a field pointing at a deleted role were all
  // representable and silently unreported before AFB-0075.
  const blockers = [
    ...templatePacketBlockers(template, bindings),
    ...placementBlockers(layoutOf(template), {
      page_count: template.page_count,
      recipient_roles: rolesOf(template),
    }),
  ];
  const savedFieldCount = layoutOf(template).length;
  const proposedMerge = proposal
    ? proposal.fields.filter((f) => f.field_class === 'merge').length
    : 0;
  const proposedSigner = proposal ? proposal.fields.length - proposedMerge : 0;
  // Restricted fields are a subset of the SIGNER ones — they are placed, and
  // they are counted here so the summary can say how many of the boxes
  // Antonela is about to save collect an answer only she and the executives
  // will be able to read.
  const proposedRestricted = proposal
    ? proposal.fields.filter((f) => f.is_sensitive === true).length
    : 0;
  // Every widget the reader dropped, across all reasons — the number of boxes
  // Antonela still has to place herself.
  const skippedTotal = proposal
    ? proposal.skippedOther.reduce((n, s) => n + s.count, 0)
    : 0;

  /**
   * Read the PDF's declared widgets and turn them into a proposed layout.
   *
   * The SAVED roles are used, not the drafts above: a field pointing at a
   * role_key that was never written to the row cannot be resolved to a
   * recipient at send time, so it would look like progress and place nothing.
   */
  async function readPdfForm() {
    setReading(true);
    setReadError(null);
    setProposal(null);
    try {
      const { pages, widgets } = await readTemplateAcroForm(template);
      setProposal(placeAcroFormFields({
        pages,
        widgets,
        roles: rolesOf(template),
        bindingKeys: offered.map((b) => b.binding_key),
      }));
    } catch (e) {
      setReadError(errorMessage(e, "This PDF's form fields could not be read."));
    }
    setReading(false);
  }

  /** Save the proposal, never silently over the top of an existing layout. */
  function acceptProposal() {
    if (!proposal || proposal.fields.length === 0) return;
    if (
      savedFieldCount > 0
      && !window.confirm(
        `"${template.name}" already has ${savedFieldCount} placed field`
        + `${savedFieldCount === 1 ? '' : 's'}. Saving these ${proposal.fields.length} `
        + `REPLACES all ${savedFieldCount}, including any merge bindings tagged on `
        + `them. Continue?`,
      )
    ) {
      return;
    }
    const fields = proposal.fields;
    setProposal(null);
    void onSaveLayout(fields);
  }

  const roleKeys = roles.map((r) => r.role_key.trim());
  const roleProblem =
    roleKeys.some((k) => k === '')
      ? 'Every role needs a key — the field layout points at it.'
      : new Set(roleKeys).size !== roleKeys.length
        ? 'Two roles share a key, so a field could not say which one it belongs to.'
        : null;

  function patchRole(index: number, patch: Partial<TemplateRecipientRole>) {
    setRoles((cur) => cur.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRole() {
    setRoles((cur) => [
      ...cur,
      {
        role_key: '',
        label: '',
        role: 'SIGNER',
        signing_order: cur.length + 1,
        auth_mode: 'emailed_link',
      },
    ]);
  }

  function patchField(index: number, patch: Partial<TemplateField>) {
    setLayout((cur) =>
      cur.map((f, i) => {
        if (i !== index) return f;
        const next = { ...f, ...patch };
        // Normalise on every edit so switching back to "signer" drops the
        // binding rather than leaving it to be written out later.
        return { ...next, ...normaliseFieldTagging(next) };
      }),
    );
  }

  /**
   * Tick or untick "Restricted" on one placed field.
   *
   * Goes through markFieldRestricted rather than setting the boolean, because
   * ticking has to force the class to signer and drop any binding in the same
   * breath — see that function. This is the ONLY way a human can mark a field
   * restricted, and it is why it exists: auto-placement can only flag what the
   * PDF names, and the account-number box on the real Direct Deposit form is
   * named "undefined". That box is placed by hand and ticked here.
   */
  function toggleFieldRestricted(index: number, restricted: boolean) {
    setLayout((cur) =>
      cur.map((f, i) => (i === index ? { ...f, ...markFieldRestricted(f, restricted) } : f)),
    );
  }

  return (
    <div className="mt-2 space-y-3 border-t border-tcs-border pt-2">
      {/* ── what it is ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={LABEL} htmlFor={`tpl-type-${template.template_id}`}>
            Document type
          </label>
          <select
            id={`tpl-type-${template.template_id}`}
            value={docType}
            disabled={busy}
            onChange={(e) => setDocType(e.target.value as EsignDocumentType)}
            className={`mt-1 ${INPUT}`}
          >
            {(Object.keys(DOCUMENT_TYPE_LABELS) as EsignDocumentType[]).map((k) => (
              <option key={k} value={k}>{DOCUMENT_TYPE_LABELS[k]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL} htmlFor={`tpl-checklist-${template.template_id}`}>
            Onboarding checklist item
          </label>
          <select
            id={`tpl-checklist-${template.template_id}`}
            value={checklistKey}
            disabled={busy}
            onChange={(e) => setChecklistKey(e.target.value)}
            className={`mt-1 ${INPUT}`}
          >
            <option value="">None — satisfies no checklist item</option>
            {Object.keys(CHECKLIST_KEY_LABELS).map((k) => (
              <option key={k} value={k}>{CHECKLIST_KEY_LABELS[k]}</option>
            ))}
            {/* A key this build does not know about still has to be visible and
                survive a save of the OTHER field. Without this option the select
                would silently render as "None" and then write it away. */}
            {checklistKey !== '' && !(checklistKey in CHECKLIST_KEY_LABELS) && (
              <option value={checklistKey}>{checklistKey} (not a known item)</option>
            )}
          </select>
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => void onSaveMeta({
          document_type: docType,
          checklist_key: checklistKey === '' ? null : checklistKey,
        })}
        className={PRIMARY}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              : <Save className="h-3.5 w-3.5" aria-hidden />}
        Save details
      </button>

      {/* ── who signs it, by role ──────────────────────────────────────── */}
      <div className="border-t border-tcs-border pt-2">
        <p className="text-[11px] font-semibold text-tcs-text">Who signs it</p>
        <p className="text-[11px] text-tcs-text-muted">
          Roles, not people. <strong>No email address is ever stored on a
          template</strong> — these are placeholders, matched to real recipients
          when an envelope is created from it, which is what makes the template
          reusable.
        </p>
        {roles.length === 0 && (
          <p className="mt-1 text-[11px] italic text-tcs-text-muted">
            No roles yet. Add one for each person who has to sign, approve or be
            copied.
          </p>
        )}
        <ul className="mt-1.5 space-y-1.5">
          {roles.map((r, i) => (
            <li
              key={`role-${i}`}
              className="rounded-md border border-tcs-border bg-tcs-bg p-2"
            >
              <div className="grid grid-cols-2 gap-1.5">
                <input
                  aria-label={`Role key ${i + 1}`}
                  value={r.role_key}
                  disabled={busy}
                  placeholder="employee"
                  onChange={(e) => patchRole(i, { role_key: e.target.value })}
                  className={INPUT}
                />
                <input
                  aria-label={`Role label ${i + 1}`}
                  value={r.label}
                  disabled={busy}
                  placeholder="Employee"
                  onChange={(e) => patchRole(i, { label: e.target.value })}
                  className={INPUT}
                />
                <select
                  aria-label={`Role type ${i + 1}`}
                  value={r.role}
                  disabled={busy}
                  onChange={(e) => patchRole(i, { role: e.target.value as RecipientRole })}
                  className={INPUT}
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  aria-label={`Signing order ${i + 1}`}
                  value={r.signing_order}
                  disabled={busy}
                  onChange={(e) => patchRole(i, {
                    signing_order: Number(e.target.value) || 1,
                  })}
                  className={INPUT}
                />
                <select
                  aria-label={`Identity check ${i + 1}`}
                  value={r.auth_mode}
                  disabled={busy}
                  onChange={(e) => patchRole(i, {
                    auth_mode: e.target.value as TemplateRecipientRole['auth_mode'],
                  })}
                  className={INPUT}
                >
                  {AUTH_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setRoles((cur) => cur.filter((_, x) => x !== i))}
                  className={`${SECONDARY} justify-center`}
                >
                  <Trash2 className="h-3 w-3" aria-hidden /> Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <button type="button" disabled={busy} onClick={addRole} className={SECONDARY}>
            <Plus className="h-3 w-3" aria-hidden /> Add role
          </button>
          <button
            type="button"
            disabled={busy || roleProblem !== null}
            onClick={() => void onSaveRoles(roles.map((r) => ({
              ...r,
              role_key: r.role_key.trim(),
              label: r.label.trim() || r.role_key.trim(),
            })))}
            className={PRIMARY}
          >
            <Save className="h-3.5 w-3.5" aria-hidden /> Save roles
          </button>
        </div>
        {roleProblem && (
          <p role="alert" className="mt-1 text-[11px] text-st-danger-fg">{roleProblem}</p>
        )}
      </div>

      {/* ── which fields we fill, and from where ───────────────────────── */}
      <div className="border-t border-tcs-border pt-2">
        <p className="text-[11px] font-semibold text-tcs-text">Where the boxes go</p>

        {/* AFB-0075 — place fields on the template's own PDF, by hand.
            Deliberately ABOVE the auto-placement panel and outside the "no
            layout yet" branch: the documents that need this most are the ones
            auto-placement cannot help at all. The handbook and the personal
            information form declare zero AcroForm widgets, so for those two
            this is the only route to a signable document that exists. */}
        {templateHasDocument(template) && (
          <TemplateFieldCanvas
            template={template}
            layout={layout}
            onChange={setLayout}
            bindings={offered}
            disabled={busy}
          />
        )}

        {/* AFB-0075 / PR 4 — read the boxes off the PDF rather than place 107 of
            them by hand. Deliberately OUTSIDE the "no layout yet" branch below:
            the case this exists for is the template that has no layout at all. */}
        {/* 2026-08-20: the document-level refusal that used to sit here is
            GONE. Direct deposit forms are auto-placed like everything else —
            the bank account box is placed as a restricted signer field, not
            withheld. Withholding it did not protect anyone; it just meant the
            form went out with no box on it. */}
        {templateHasDocument(template) && (
          <div className="mt-1.5 rounded-md border border-tcs-border bg-tcs-bg p-2">
            <p className="text-[11px] text-tcs-text-muted">
              This PDF may already declare its own form fields. Reading them puts
              a box wherever the <strong>document itself</strong> says one is, so
              this becomes a review rather than a hundred drag-and-drops. Nothing
              is saved until you have read the summary.
            </p>
            <button
              type="button"
              disabled={busy || reading}
              onClick={() => void readPdfForm()}
              className={`mt-1.5 ${SECONDARY}`}
            >
              {reading
                ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                : <ScanLine className="h-3 w-3" aria-hidden />}
              Place fields from this PDF&rsquo;s form
            </button>
            {readError && (
              <p role="alert" className="mt-1 text-[11px] text-st-danger-fg">
                {readError}
              </p>
            )}
            {proposal && (
              <div className="mt-1.5 border-t border-tcs-border pt-1.5">
                {proposal.blockers.length > 0 ? (
                  <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-st-warn-fg">
                    {proposal.blockers.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                ) : proposal.fields.length === 0 ? (
                  <p className="text-[11px] text-tcs-text-secondary">
                    Nothing to place — this PDF declares no form fields this can
                    use. Place them by hand on the prep screen instead.
                  </p>
                ) : (
                  <>
                    <p className="text-[11px] font-medium text-tcs-text">
                      {proposal.fields.length} field
                      {proposal.fields.length === 1 ? '' : 's'} ready to review —{' '}
                      {proposedMerge} we prefill, {proposedSigner} the signer fills.
                    </p>
                    <p className="text-[11px] text-tcs-text-muted">
                      {countByType(proposal.fields)
                        .map((t) => `${t.count} × ${FIELD_LABEL[t.type]}`)
                        .join(' · ')}
                    </p>
                    {savedFieldCount > 0 && (
                      <p className="mt-1 text-[11px] text-st-warn-fg">
                        This template already has {savedFieldCount} placed field
                        {savedFieldCount === 1 ? '' : 's'}. Saving replaces{' '}
                        {savedFieldCount === 1 ? 'it' : 'all of them'}, merge
                        tagging included — you will be asked to confirm first.
                      </p>
                    )}
                  </>
                )}
                {proposal.restrictedFields.length > 0 && (
                  <div className="mt-1.5">
                    <p className="text-[11px] font-medium text-st-warn-fg-strong">
                      {proposedRestricted} of these field
                      {proposedRestricted === 1 ? ' is' : 's are'} restricted
                    </p>
                    <p className="text-[11px] text-tcs-text-secondary">
                      The employee fills{' '}
                      {proposal.restrictedFields.length === 1 ? 'this one' : 'these'} in
                      themselves — nothing is ever prefilled into{' '}
                      {proposal.restrictedFields.length === 1 ? 'it' : 'them'} — and once
                      it comes back the answer is visible only to you and the
                      executives. It is not shown on the packet review, the prep
                      screen, or to anyone else with a login:{' '}
                      {proposal.restrictedFields.join(', ')}.
                    </p>
                  </div>
                )}
                {proposal.skippedOther.length > 0 && (
                  <div className="mt-1.5 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2">
                    {/* Not a footnote. On the real Direct Deposit form the
                        ACCOUNT NUMBER box is one of the "unnamed" ones, because
                        the PDF literally calls it "undefined". If this reads as
                        a diagnostic she scrolls past it, places the box by hand
                        and never ticks Restricted — and the account number
                        lands in a column every employee can read. */}
                    <p className="text-[11px] font-semibold text-st-warn-fg-strong">
                      {skippedTotal} box{skippedTotal === 1 ? '' : 'es'} still to place by
                      hand
                    </p>
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-st-warn-fg-strong">
                      {proposal.skippedOther.map((s) => (
                        <li key={s.reason}>
                          {s.count} {s.reason}
                          {s.pages.length > 0 && (
                            <> — {s.pages.length === 1 ? 'page' : 'pages'} {s.pages.join(', ')}</>
                          )}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-[11px] text-st-warn-fg-strong">
                      Save these fields first, then open the document above and
                      drag a box onto each one. <strong>If any of them collects a
                      Social Security number, a date of birth, or a bank account
                      or routing number, tick &ldquo;Restricted&rdquo; on it in
                      the list below</strong> — that is the only way those
                      answers stay visible to you and the executives alone.
                    </p>
                  </div>
                )}
                {proposal.unhandledPages.length > 0 && (
                  <p className="mt-1.5 text-[11px] text-st-warn-fg">
                    {proposal.unhandledPages.length === 1 ? 'Page' : 'Pages'}{' '}
                    {proposal.unhandledPages.join(', ')} could not be read —{' '}
                    {proposal.unhandledPages.length === 1 ? 'it is' : 'they are'}{' '}
                    rotated, and a rotated page is left for you to place by hand
                    rather than transformed and got subtly wrong.
                  </p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {proposal.fields.length > 0 && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={acceptProposal}
                      className={PRIMARY}
                    >
                      <Save className="h-3.5 w-3.5" aria-hidden /> Save these{' '}
                      {proposal.fields.length} fields
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setProposal(null)}
                    className={SECONDARY}
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {layout.length === 0 ? (
          <p className="mt-1 text-[11px] italic text-tcs-text-muted">
            No fields placed yet.{' '}
            {templateHasDocument(template)
              ? 'Open the document above and drag the boxes onto it, or read them '
                + 'out of the PDF if it declares its own form fields. Either way '
                + 'you do it once and every packet built from this template '
                + 'carries them.'
              : 'Attach the PDF above first — there is nothing to place boxes on '
                + 'until this template carries its document.'}
          </p>
        ) : (
          <>
            <p className="text-[11px] text-tcs-text-muted">
              A captured layout is all signer fields. Say which ones we prefill,
              and what from — the list is the registry of allowed values, so a
              value that is not on it cannot be typed in.
            </p>
            {bindingsError && (
              <p role="alert" className="mt-1 text-[11px] text-st-danger-fg">
                The list of values could not be loaded ({bindingsError}), so merge
                fields cannot be tagged right now.
              </p>
            )}
            <ul className="mt-1.5 space-y-1.5">
              {layout.map((f, i) => {
                const tagging = normaliseFieldTagging(f);
                return (
                  <li
                    key={`field-${i}`}
                    className="rounded-md border border-tcs-border bg-tcs-bg p-2"
                  >
                    <p className="text-[11px] text-tcs-text">
                      <span className="font-medium">
                        {i + 1}. {FIELD_LABEL[f.field_type]}
                      </span>{' '}
                      <span className="text-tcs-text-muted">
                        · {describePlacement(f)}
                        {f.role_key ? ` · ${f.role_key}` : ''}
                      </span>
                      {tagging.is_sensitive && (
                        <span className="ml-1 rounded-full bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
                          Restricted — HR and executives only
                        </span>
                      )}
                    </p>
                    <div className="mt-1 grid grid-cols-2 gap-1.5">
                      {/* A restricted field can only ever be signer-filled, so
                          the choice is not offered rather than offered and then
                          refused by the database. "We prefill it" on an SSN is
                          the one thing the ruling forbids outright. */}
                      <select
                        aria-label={`Who fills field ${i + 1}`}
                        value={tagging.field_class}
                        disabled={busy || tagging.is_sensitive}
                        onChange={(e) => patchField(i, {
                          field_class: e.target.value as TemplateFieldClass,
                        })}
                        className={INPUT}
                      >
                        {FIELD_CLASSES
                          .filter((c) => !tagging.is_sensitive || c.value === 'signer')
                          .map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                      </select>
                      {tagging.field_class === 'merge' && !tagging.is_sensitive && (
                        <select
                          aria-label={`Value for field ${i + 1}`}
                          value={tagging.binding ?? ''}
                          disabled={busy || offered.length === 0}
                          onChange={(e) => patchField(i, {
                            binding: e.target.value === '' ? null : e.target.value,
                          })}
                          className={INPUT}
                        >
                          <option value="">Choose a value…</option>
                          {offered.map((b) => (
                            <option key={b.binding_key} value={b.binding_key}>
                              {b.label}
                            </option>
                          ))}
                          {/* A binding that was retired after it was tagged, or
                              one the list could not load. Shown so a save of
                              some other field cannot quietly blank it — the
                              readiness list below already names it as a gap. */}
                          {tagging.binding !== null
                            && !offered.some((b) => b.binding_key === tagging.binding) && (
                            <option value={tagging.binding}>
                              {tagging.binding} (not available)
                            </option>
                          )}
                        </select>
                      )}
                    </div>
                    {/* THE MANUAL RESTRICTED TICK. Auto-placement can only flag
                        what the PDF names, and the account-number box on the
                        real Direct Deposit form is named "undefined" — so
                        without this control the one field that matters most has
                        no way to be marked. */}
                    <label className="mt-1.5 flex items-start gap-2 text-[11px] text-tcs-text">
                      <input
                        type="checkbox"
                        checked={tagging.is_sensitive}
                        disabled={busy}
                        onChange={(e) => toggleFieldRestricted(i, e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0"
                      />
                      <span>
                        <span className="font-medium">
                          Restricted — only HR and executives can see the answer
                        </span>
                        <span className="block text-tcs-text-muted">
                          For a Social Security number, date of birth, or bank
                          account and routing number. The employee types it in
                          themselves: we never fill it in for them, and it is
                          never shown on the packet review or the prep screen.
                          {tagging.is_sensitive
                            ? ' Untick it if this box is not actually private.'
                            : ''}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSaveLayout(
                layout.map((f) => ({ ...f, ...normaliseFieldTagging(f) })),
              )}
              className={`mt-1.5 ${PRIMARY}`}
            >
              <Save className="h-3.5 w-3.5" aria-hidden /> Save these{' '}
              {layout.length} field{layout.length === 1 ? '' : 's'}
            </button>
          </>
        )}
      </div>

      {/* ── is it packet-ready? Read off the SAVED row, not the drafts. ── */}
      <div className="border-t border-tcs-border pt-2">
        {blockers.length === 0 ? (
          <p className="text-[11px] text-st-ok-fg">
            Packet-ready — document saved, {template.page_count} page
            {template.page_count === 1 ? '' : 's'}, every merge field tagged.
          </p>
        ) : (
          <>
            <p className="text-[11px] font-medium text-st-warn-fg">
              Not packet-ready yet:
            </p>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-tcs-text-secondary">
              {blockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
